import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import AllowedFor from '@/models/common/AllowedFor';
import { SupportRequestFormData, NewChildData, createNewChildSchema } from '../schemas/supportRequestFormSchema';
import { StudyTimeOptions } from '@/constants/studyTimeOptions';
import Checkbox from '@/components/ui/Checkbox/Checkbox';
import RadioGroup, { RadioOption } from '@/pages/newSupported/components/RadioGroup';
import FileUpload from '@/pages/newSupported/components/FileUpload';
import LabeledInput from '@/pages/newSupported/components/LabeledInput';
import Toggle from '@/pages/newSupported/components/Toggle';
import { useEffect, useState } from 'react';
import { ChildBasicDetailsDtoIn } from '@/models/in/ChildBasicDetailsDtoIn';
import { RelationshipStatus } from '@/models/common/RelationshipStatus';
import { usePostAuthApi } from '@/hooks/UseApiCalls';
import { queryKeys } from '@/hooks/QueryKeys';
import { FileReaderHelper, translateFileReadError, EmptyFileError, FileReadError } from '@/helpers/fileReaderHelper';
import { ChildDtoIn } from '@/models/in/ChildDtoIn';
import { AddChildrenDtoOut } from '@/models/out/AddChildrenDtoOut';
import useFundContext from '@/contexts/Fund/useFundContext';
import approvedSupport from '@/assets/icons/approved-support.svg';
import addIcon from '@/assets/icons/add.svg';
import minusIcon from '@/assets/icons/minus.svg';
import styles from './RequiredDetails.module.scss';
import Button from '@/components/ui/Button';
import { MIN_BIRTH_DATE_INPUT } from '@/helpers/dateHelper';
import { toHebrewName } from '@/helpers/hebrewName';
import { requiresDocumentDate } from '@/helpers/supportDocumentDate';
import DocumentDateField from '../DocumentDateField';

interface ChildCardProps {
  child: any;
  isSelected: boolean;
  onSelect: (childId: number) => void;
  disabled?: boolean;
  disabledTitle?: string;
}

const ChildCard = ({ child, isSelected, onSelect, disabled = false, disabledTitle }: ChildCardProps) => {
  return (
    <button
      type="button"
      onClick={() => onSelect(child.id)}
      disabled={disabled}
      title={disabled ? disabledTitle : undefined}
      className={`${styles.childCard} ${isSelected ? styles.selected : styles.unselected} ${disabled ? styles.disabled : ''}`}
    >
      <div className="flex justify-between items-start">
        <div>
          <div className="body-md-semibold text-(--font-body-1) mb-2">
            {child.firstName} {child.lastName}
          </div>
          <div className="body-sm-regular text-(--font-body-2)">
            {child.israeliId}
          </div>
        </div>
        {child.status === RelationshipStatus.Approved && (
          <img src={approvedSupport} alt="" className="w-5 h-5 mt-0.5" />
        )}
      </div>
    </button>
  );
};

interface RequiredDetailsProps {
  children: ChildBasicDetailsDtoIn[];
}

const RequiredDetails = ({ children }: RequiredDetailsProps) => {
  const { t } = useTranslation();
  const { currentFund } = useFundContext();
  const queryClient = useQueryClient();
  const { watch, setValue } = useFormContext<SupportRequestFormData>();
  const selectedGoal = watch('goalWithLimits')?.goal;
  const supported = watch('supported');
  
  // Get IDs from context and form
  const fundId = currentFund?.id;
  const supportedId = supported?.id;
  const supportRecipient = watch('supportRecipient');
  const childId = watch('childId');
  const weddingDate = watch('weddingDate');
  const documentDate = watch('documentDate');
  const documents = watch('documents') || [];
  const additionalDocuments = watch('additionalDocuments') || [];
  // Kollel-specific fields
  const kollelName = watch('kollelName');
  const studyTimes = watch('studyTimes') || [];
  const kollelAddress = watch('kollelAddress');
  const studyMaterial = watch('studyMaterial');
  const roshKollelName = watch('roshKollelName');

  // Local state for new child form (not part of the form schema)
  const [newChild, setNewChild] = useState<NewChildData | null>(null);

  // API for saving children
  const addChildrenApiUrl = fundId && supportedId ? `funds/${fundId}/supporteds/${supportedId}/children` : '';
  const { mutateAsync: addChildren, isPending: isSavingChild } = usePostAuthApi<ChildDtoIn[], AddChildrenDtoOut>(addChildrenApiUrl);

  // Check if this is a Wedding goal
  const isWeddingGoal = selectedGoal?.name === 'Wedding';
  // Bar Mitzvah support can only be requested for a child whose relationship was approved
  const isBarMitzvahGoal = selectedGoal?.name === 'BarMitzvah';
  // Check if this is a Kollel goal
  const isKollelGoal = selectedGoal?.name === 'Kollel';
  // SPR-10015: the office checks the document's date against the submission date
  const needsDocumentDate = requiresDocumentDate(selectedGoal);

  // Set supportRecipient automatically based on AllowedFor
  // IMPORTANT: This useEffect must be BEFORE any early returns to avoid hooks order issues
  useEffect(() => {
    if (selectedGoal && !supportRecipient) {
      if (selectedGoal.allowedFor === AllowedFor.Supported) {
        setValue('supportRecipient', 'Supported');
      } else if (selectedGoal.allowedFor === AllowedFor.SupportedChildren) {
        setValue('supportRecipient', 'SupportedChildren');
      } else if (selectedGoal.allowedFor === AllowedFor.SupportedAndSupportedChildren) {
        setValue('supportRecipient', 'SupportedChildren');
      } else if (selectedGoal.allowedFor === AllowedFor.Fund) {
        setValue('supportRecipient', 'Fund');
      }
    }
  }, [selectedGoal?.id, supportRecipient, setValue]);

  // Handlers for new child (local state)
  const handleAddNewChild = () => {
    setNewChild({
      firstName: '',
      israeliId: '',
      birthDate: null,
      livesWithSupported: true,
      idAppendixFile: null,
    });
    // Clear childId when adding a new child (user is not selecting existing)
    setValue('childId', null);
  };

  const handleRemoveNewChild = () => {
    setNewChild(null);
  };

  const handleNewChildFieldChange = (field: keyof NewChildData, value: any) => {
    if (newChild) {
      setNewChild({ ...newChild, [field]: value });
    }
  };

  // Save the new child to the database
  const handleSaveChild = async () => {
    if (!newChild) return;
    
    // Validate using schema
    const schema = createNewChildSchema(t);
    const result = schema.safeParse(newChild);
    
    if (!result.success) {
      const errorMessages = result.error.issues.map((issue) => issue.message);
      alert(errorMessages.join('\n'));
      return;
    }

    try {
      // Convert ID appendix file to base64
      const convertedDocuments = newChild.idAppendixFile 
        ? await FileReaderHelper.fileToDocumentDtoOut(newChild.idAppendixFile, 'IdAppendix')
        : [];

      // Prepare request data with children and documents separately
      const requestData: AddChildrenDtoOut = {
        children: [{
          firstName: newChild.firstName,
          israeliId: newChild.israeliId,
          birthDate: newChild.birthDate ? new Date(newChild.birthDate) : undefined,
          livesWithSupported: newChild.livesWithSupported,
        }],
        documents: Array.isArray(convertedDocuments) ? convertedDocuments : [convertedDocuments],
      };

      // Call API to save the child
      const savedChildren = await addChildren(requestData);
      
      // Clear the new child form
      setNewChild(null);

      // Refresh the children list
      if (fundId && supportedId) {
        await queryClient.invalidateQueries({
          queryKey: queryKeys.supporteds.additionalInfo(fundId, supportedId),
        });
      }

      // A newly-created child is pending approval and cannot be selected for Bar Mitzvah yet.
      if (!isBarMitzvahGoal && savedChildren && savedChildren.length > 0 && savedChildren[0].id) {
        setValue('childId', savedChildren[0].id);
      }

      alert(t(isBarMitzvahGoal ? 'childSavedPendingApprovalForBarMitzvah' : 'childSavedSuccessfully'));
    } catch (error: unknown) {
      if (error instanceof EmptyFileError || error instanceof FileReadError) {
        alert(translateFileReadError(error, t));
        return;
      }
      const axiosError = error as { response?: { data?: string }; message?: string };
      const errorMessage = axiosError?.response?.data || axiosError?.message || t('errorSavingChild');
      alert(errorMessage);
    }
  };

  const handleAddAdditionalDocument = () => {
    setValue('additionalDocuments', [...additionalDocuments, { file: null, customDescription: '' }]);
  };

  const handleRemoveAdditionalDocument = (docIndex: number) => {
    setValue('additionalDocuments', additionalDocuments.filter((_, i) => i !== docIndex));
  };

  const handleAdditionalDocumentChange = (
    docIndex: number,
    value: { file: File | null; customDescription: string }
  ) => {
    const updated = [...additionalDocuments];
    updated[docIndex] = value;
    setValue('additionalDocuments', updated);
  };

  if (!selectedGoal) {
    return <p>{t('pleaseSelectGoalFirst')}</p>;
  }

  const recipientOptions: RadioOption[] = [
    {
      value: 'SupportedChildren',
      label: t('supportedChild'),
    },
    {
      value: 'Supported',
      label: isWeddingGoal ? t('supportedThemselves') : t('supported'),
    },
  ];

  const shouldShowChildSelection =
    selectedGoal.allowedFor === AllowedFor.SupportedChildren ||
    (selectedGoal.allowedFor === AllowedFor.SupportedAndSupportedChildren &&
      supportRecipient === 'SupportedChildren');

  return (
    <div className="w-full max-w-[1132px] px-4">
      <h2 className="body-lr-bold text-(--font-sub-title-2)">
        {t('requiredDetailsForSupportGoal')}
      </h2>

      {selectedGoal.allowedFor === AllowedFor.SupportedAndSupportedChildren && (
        <div className="mt-4">
          <h3 className="body-md-medium text-(--font-body-3) mb-2">
            {isWeddingGoal ? t('whoIsGettingMarried') : t('forWhomIsTheSupport')}
          </h3>
          <RadioGroup
            options={recipientOptions}
            value={supportRecipient || ''}
            onChange={(value) => setValue('supportRecipient', value)}
            name="supportRecipient"
            direction="horizontal"
          />
        </div>
      )}

      {shouldShowChildSelection && (
        <div className="mt-6">
          <h3 className="body-md-medium text-(--font-body-3) mb-2">
            {t('selectChild')}
          </h3>
          {isBarMitzvahGoal && (
            <p className="body-sm-regular text-(--font-body-2) mb-3">
              {t('verifiedChildRequiredForBarMitzvah')}
            </p>
          )}
          
          {/* Existing children cards */}
          {children.length > 0 && (
            <div className={styles.childrenContainer}>
              {children.map((child) => (
                <ChildCard
                  key={child.id}
                  child={child}
                  isSelected={childId === child.id && !newChild}
                  onSelect={(id) => {
                    setValue('childId', id);
                    setNewChild(null); // Clear new child form when selecting existing
                  }}
                  disabled={isBarMitzvahGoal && child.status !== RelationshipStatus.Approved}
                  disabledTitle={t('verifiedChildRequiredForBarMitzvah')}
                />
              ))}
            </div>
          )}

          {/* New child form */}
          {newChild && (
            <div
              className="bg-(--surface-grey-lowest) border border-(--border-lowest) rounded-2xl p-8 max-md:p-4 relative shadow-sm mt-4"
            >
              <div className="flex flex-col gap-6">
                <div className={styles.newChildFormRow}>
                  <LabeledInput
                    label="firstName"
                    id="newChildFirstName"
                    value={newChild.firstName}
                    onChange={(e) => handleNewChildFieldChange('firstName', toHebrewName(e.target.value))}
                    required
                  />
                  <LabeledInput
                    label="israeliId"
                    id="newChildId"
                    value={newChild.israeliId}
                    onChange={(e) => handleNewChildFieldChange('israeliId', e.target.value)}
                    required
                  />
                  <LabeledInput
                    label="birthDate"
                    id="newChildBirthDate"
                    type="date"
                    min={MIN_BIRTH_DATE_INPUT}
                    value={newChild.birthDate || ''}
                    onChange={(e) => handleNewChildFieldChange('birthDate', e.target.value || null)}
                    required
                  />
                  <Toggle
                    label={t('livesWithSupportedQuestion')}
                    value={newChild.livesWithSupported}
                    onChange={(value) => handleNewChildFieldChange('livesWithSupported', value)}
                  />
                </div>
                {/* ID Appendix upload - required */}
                <div className="w-full max-w-[400px]">
                  <FileUpload
                    placeholder={t('idAppendix')}
                    value={newChild.idAppendixFile}
                    onChange={(file) => handleNewChildFieldChange('idAppendixFile', file)}
                  />
                </div>
                {/* Save child button */}
                <Button
                  variant="primary"
                  onClick={handleSaveChild}
                  disabled={isSavingChild}
                  className="self-end"
                >
                  {isSavingChild ? t('saving') : t('saveChild')}
                </Button>
              </div>
              {/* Remove button */}
              <button
                type="button"
                onClick={handleRemoveNewChild}
                className="absolute start-[-12px] top-6 w-6 h-6 bg-(--font-error) rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
                title={t('removeChild')}
              >
                <img src={minusIcon} alt="" />
              </button>
            </div>
          )}
          
          {/* Add child button - only show when no new child form is open */}
          {!newChild && (
            <Button
              variant="outline"
              onClick={handleAddNewChild}
              className="mt-4 flex items-center gap-1"
            >
              <img src={addIcon} alt="" />
              {t('addAnotherChild')}
            </Button>
          )}
        </div>
      )}

      {/* Wedding date field - only for Wedding goal, after child selection */}
      {isWeddingGoal && (
        <div className="mt-6">
          <DocumentDateField
            label="weddingDate"
            id="weddingDate"
            value={weddingDate}
            onChange={(date) => setValue('weddingDate', date)}
            allowHebrew
          />
        </div>
      )}

      {/* Kollel-specific fields - only for Kollel goal */}
      {isKollelGoal && (
        <div className="mt-6">
          <h3 className="body-md-medium text-(--font-body-3) mb-4">
            {t('kollelDetails')}
          </h3>
          
          <div className="flex flex-col gap-6">
            {/* First row: Kollel name and Rosh Kollel name */}
            <div className="flex gap-8">
              <LabeledInput
                label="kollelName"
                id="kollelName"
                value={kollelName || ''}
                onChange={(e) => setValue('kollelName', e.target.value)}
                required
                width="300px"
              />
              <LabeledInput
                label="roshKollelName"
                id="roshKollelName"
                value={roshKollelName || ''}
                onChange={(e) => setValue('roshKollelName', e.target.value)}
                required
                width="300px"
              />
            </div>

            {/* Second row: Kollel address */}
            <div className="flex gap-8">
              <LabeledInput
                label="kollelAddress"
                id="kollelAddress"
                value={kollelAddress || ''}
                onChange={(e) => setValue('kollelAddress', e.target.value)}
                required
                width="500px"
              />
            </div>

            {/* Third row: Study material */}
            <div className="flex gap-8">
              <LabeledInput
                label="studyMaterial"
                id="studyMaterial"
                value={studyMaterial || ''}
                onChange={(e) => setValue('studyMaterial', e.target.value)}
                required
                width="500px"
              />
            </div>

            {/* Fourth row: Study times (multi-select checkboxes) */}
            <div>
              <label className="body-sm-regular text-(--font-heading) block mb-2">
                {t('studyTimes')}
                <span className="text-red-500"> *</span>
              </label>
              <div className="flex flex-wrap gap-4">
                {Object.values(StudyTimeOptions).map((option) => (
                  <label
                    key={option}
                    className="flex items-center gap-2 cursor-pointer body-md-regular text-(--font-body-1)"
                  >
                    <Checkbox
                      checked={studyTimes.includes(option)}
                      onCheckedChange={(checked) => {
                        const currentTimes = studyTimes || [];
                        const newTimes = checked
                          ? [...currentTimes, option]
                          : currentTimes.filter((t: string) => t !== option);
                        setValue('studyTimes', newTimes);
                      }}
                    />
                    {t(`StudyTimeOptions.${option}`)}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedGoal.requiredDocuments && selectedGoal.requiredDocuments.length > 0 && (
        <div className="mt-6">
          <h2 className="body-lr-bold text-(--font-sub-title-2)">
            {t('uploadDocumentsAndApprovals')}
          </h2>
          <div className={styles.documentsContainer}>
            {selectedGoal.requiredDocuments.map((requiredDoc: string, docIndex: number) => {
              const fileForThisRequiredDoc = documents
                .find((doc) => doc.requiredDocName === requiredDoc)?.file || null;

              return (
                <FileUpload
                  key={docIndex}
                  label={requiredDoc}
                  placeholder={t(`GoalsRequiredDocuments.${requiredDoc}`)}
                  value={fileForThisRequiredDoc}
                  onChange={(file) => {
                    const currentDocs = documents || [];

                    const filteredDocs = currentDocs.filter(
                      (doc) => doc.requiredDocName !== requiredDoc
                    );

                    const updatedDocuments = file
                      ? [...filteredDocs, { file, requiredDocName: requiredDoc }]
                      : filteredDocs;

                    setValue('documents', updatedDocuments);
                  }}
                />
              );
            })}

            {additionalDocuments.map((doc, docIndex) => (
              <FileUpload
                key={`additional-${docIndex}`}
                placeholder={t('documentName')}
                value={doc.file}
                onChange={(file) =>
                  handleAdditionalDocumentChange(docIndex, {
                    file,
                    customDescription: doc.customDescription || '',
                  })
                }
                customDescription={{
                  description: doc.customDescription || '',
                  onDescriptionChange: (description) =>
                    handleAdditionalDocumentChange(docIndex, {
                      file: doc.file,
                      customDescription: description,
                    }),
                  onRemove: () => handleRemoveAdditionalDocument(docIndex),
                }}
              />
            ))}

            {needsDocumentDate && (
              <DocumentDateField
                label="documentDate.label"
                id="documentDate"
                value={documentDate}
                onChange={(date) => setValue('documentDate', date)}
              />
            )}

            <button
              type="button"
              onClick={handleAddAdditionalDocument}
              className="flex items-center justify-center gap-2 h-11 px-4 py-2 rounded-md border border-dashed border-(--border-primary) bg-(--surface-action) body-sm-semibold text-(--font-primary) hover:bg-(--surface-primary-lowest) transition-colors"
            >
              <img src={addIcon} alt="" />
              {t('addAdditionalDocument')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequiredDetails;

